'use client';

import { defineMessages, useIntl } from 'react-intl';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { buttonVariants } from '@packages/shared/src/ui/Button/Button.variants';
import { useLoyaltyApi } from '@packages/shared/src/hooks/useLoyaltyApi/useLoyaltyApi';
import useSWR from 'swr';
import { useLoginState } from '@packages/shared/src/hooks/useLoginState/useLoginState';
import { Link } from '@packages/modules/src/Link/Link';
import { cn } from '@packages/shared/src/utils/cn/cn';

const messages = defineMessages({
  PaybackConnectionStatusCardHeadline: {
    defaultMessage: 'Mein {companyName} Konto mit PAYBACK verknüpfen und Vorteile nutzen',
    id: 'Payback.DisconnectedStatus.Headline',
  },
  Benefit1: {
    defaultMessage:
      'PAYBACK Coupons hier im "Mein Konto" Bereich aktivieren, direkt bei der Bestellung einlösen und Punkte sammeln',
    id: 'Payback.Benefit.First',
  },
  Benefit2: {
    defaultMessage: 'Automatisch Punkte beim Einkauf auf {companyName} sammeln',
    id: 'Payback.Benefit.Second',
  },
  Benefit3: {
    defaultMessage: 'Anzeige des PAYBACK °Punktestandes',
    id: 'Payback.Benefit.Third',
  },
  Benefit4: {
    defaultMessage: 'Personalisierte Produktempfehlungen',
    id: 'Payback.Benefit.Fourth',
  },
  ConnectPaybackButton: {
    defaultMessage: 'Jetzt Konten verknüpfen',
    id: 'Payback.Disconnected.Button',
  },
});
const SuccessIcon = () => (
  <span className="icon-emp-checkmark bg-color-success h-3 w-3 flex-none" />
);

const listClassName = 'text-size-body-sm flex gap-0.5';
type MyAccountContainerProps = {
  testId?: string;
  enableSignIn?: boolean;
};
const buttonColor = 'primary';
const buttonClassName = 'text-size-body-sm items-center justify-start';
export const PaybackDisconnected = ({
  testId = 'myaccount-payback-disconnected',
  enableSignIn = true,
}: MyAccountContainerProps) => {
  const { loggedIn } = useLoginState();
  const { locale, formatMessage } = useIntl();
  const loyaltyApi = useLoyaltyApi();

  const { data } = useSWR(
    loyaltyApi && loggedIn && enableSignIn ? 'getLoyaltyLoginUrl' : null,
    () =>
      loyaltyApi?.getLoyaltyLoginUrl({
        ecLocale: locale,
      }),
    { revalidateOnFocus: false },
  );
  const {
    company: { name: companyName },
  } = useConfig();
  return (
    <div
      data-testid={testId}
      className="flex w-full flex-col items-center justify-center gap-1.5 px-1.5 py-2.5 md:gap-2 md:p-3"
    >
      <span className="icon-emp-company-logo-payback h-[3.5rem] w-[6.5rem]" />
      <p className="text-size-body-base text-center">
        {formatMessage(messages.PaybackConnectionStatusCardHeadline, { companyName })}
      </p>
      <ul className="text-size-body-sm flex flex-col gap-0.5">
        {new Array(4).fill(null).map((_, index) => (
          // eslint-disable-next-line @eslint-react/no-array-index-key
          <li className={listClassName} key={index}>
            <SuccessIcon />
            {formatMessage(messages[`Benefit${index + 1}` as keyof typeof messages], {
              companyName,
            })}
          </li>
        ))}
      </ul>
      {data?.url ? (
        <Link
          aria-disabled
          href={data.url}
          color="inherit"
          className={cn(buttonVariants({ color: buttonColor }), buttonClassName)}
        >
          {formatMessage(messages.ConnectPaybackButton)}
        </Link>
      ) : (
        <Button disabled color={buttonColor} className={buttonClassName}>
          <p>{formatMessage(messages.ConnectPaybackButton)}</p>
        </Button>
      )}
    </div>
  );
};
